import React, { useState, useRef, useEffect } from 'react';
import { CurrentRunRateData } from '../types';
import apiService from '../services/api';

const CurrentRunRate: React.FC = () => {
  // Currency formatting helper
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const [uploadedData, setUploadedData] = useState<CurrentRunRateData[]>([]);
  const [currentRunRateData, setCurrentRunRateData] = useState<CurrentRunRateData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCostRecord, setEditingCostRecord] = useState<CurrentRunRateData | null>(null);
  const [viewingCostRecord, setViewingCostRecord] = useState<CurrentRunRateData | null>(null);
  const [availableApplications, setAvailableApplications] = useState<any[]>([]);
  const [filterText, setFilterText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const updateFileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedUpdateData, setUploadedUpdateData] = useState<any[]>([]);
  const [showUpdatePreview, setShowUpdatePreview] = useState(false);
  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);
  const [pendingRecalcValue, setPendingRecalcValue] = useState(0);
  const recalcPendingRef = React.useRef(false);
  const [activeCostField, setActiveCostField] = useState<string | null>(null);
  const [activeCostFieldValue, setActiveCostFieldValue] = useState<string>('');

  const [formData, setFormData] = useState({
    applicationId: '',
    applicationName: '',
    totalAnnualCost: 0,
    annualSoftwareLicensingFees: 0,
    annualThirdPartyVendorSupport: 0,
    annualInfrastructureCost: 0,
    indirectOperationalCosts: 0,
    cloudSaasSubscriptionCostImpact: 0,
    tcoStatus: 'Not Defined' as string
  });

  // Load data from API on component mount
  useEffect(() => {
    loadCurrentRunRateData();
    loadAvailableApplications();
  }, []);

  const loadAvailableApplications = async () => {
    try {
      const apps = await apiService.getAvailableApplicationsForCosts();
      setAvailableApplications(apps);
    } catch (error: any) {
      console.error('❌ CurrentRunRate: Error loading available applications:', error);
      console.error('❌ CurrentRunRate: Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
    }
  };

  const loadCurrentRunRateData = async () => {
    setLoading(true);
    try {
      const data = await apiService.getCurrentRunRateData();
      // Data is already transformed to camelCase in the API service
      setCurrentRunRateData(data);
    } catch (error: any) {
      console.error('❌ CurrentRunRate: Error loading current run rate data:', error);
      console.error('❌ CurrentRunRate: Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        stack: error?.stack
      });
      alert(`Error loading current run rate data: ${error?.message || error}. Please check the server connection.`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a CSV file (.csv)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        let text = event.target?.result as string;
        
        // Handle potential encoding issues
        if (text.includes('�') || text.includes('\uFFFD')) {
          console.warn('Detected encoding issues, attempting to re-read with different encoding');
          text = text.replace(/[^\x00-\x7F]/g, '');
        }
        
        // Handle BOM (Byte Order Mark) if present
        if (text.charCodeAt(0) === 0xFEFF) {
          text = text.slice(1);
        }
        
        // Normalize line endings
        text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        
        // Optimized CSV parsing specifically for comma-delimited CSV files
        const parseCSVLine = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;
          let quoteChar = '';
          
          console.log(`🔍 PARSING CSV LINE: "${line}"`);
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            // Handle quote characters (both single and double quotes)
            if ((char === '"' || char === "'") && !inQuotes) {
              inQuotes = true;
              quoteChar = char;
              // Don't add the opening quote to the value
            } else if (char === quoteChar && inQuotes) {
              // Check for escaped quotes (double quotes)
              if (i + 1 < line.length && line[i + 1] === quoteChar) {
                current += char; // Add one quote character for escaped quote
                i++; // Skip the next quote
              } else {
                inQuotes = false;
                quoteChar = '';
                // Don't add the closing quote to the value
              }
            } else if (char === ',' && !inQuotes) {
              // Found delimiter outside of quotes - end current field
              result.push(current.trim());
              current = '';
            } else if (char !== '\r') {
              // Add character to current field (skip carriage returns)
              current += char;
            }
          }
          
          // Add the last field
          result.push(current.trim());
          
          console.log(`   Field count: ${result.length}`);
          
          return result;
        };

        const allLines = text.split('\n');
        console.log('All lines in file:', allLines.length);
        
        // More strict filtering - only keep lines that have actual content
        const lines = allLines.filter(line => {
          const trimmed = line.trim();
          // Skip completely empty lines or lines with only delimiters
          if (!trimmed || trimmed === '' || trimmed === ',' || trimmed === ',,' || trimmed === ',,,' || trimmed === ',,,,' || trimmed === ',,,,,' || trimmed === ',,,,,,' || trimmed === ',,,,,,,' || trimmed === ',,,,,,,') {
            return false;
          }
          return true;
        });
        
        console.log('Filtered lines (after removing empty):', lines.length);
        
        if (lines.length < 2) {
          alert('File appears to be empty or contains only headers. Please check your file format.');
          return;
        }

        const headers = parseCSVLine(lines[0]).map(h => 
          h.replace(/['"]/g, '').trim()
        );
        console.log('🎯 PARSED HEADERS ANALYSIS:');
        console.log('Raw headers:', headers);
        console.log('Header count:', headers.length);
        console.log('Headers with indexes:');
        headers.forEach((header, index) => {
          console.log(`  [${index}]: "${header}" (length: ${header.length})`);
        });
        
        console.log('\n🔍 EXPECTED VS ACTUAL HEADERS:');
        const expectedHeaders = [
          'Application ID',
          'Application Name', 
          'Total Annual Cost to Operate and Support',
          'Annual Software Licensing Fees',
          'Annual 3rd Party Vendor Support Cost',
          'Annual Infrastructure Cost',
          'Indirect Operational Costs',
          'Cloud/SaaS Subscription Costs'
        ];
        
        expectedHeaders.forEach((expected, index) => {
          const actual = headers[index] || 'MISSING';
          const match = actual === expected ? '✅' : '❌';
          console.log(`  ${match} Expected: "${expected}"`);
          console.log(`     Actual:   "${actual}"`);
        });
        
        console.log('Since column names might vary, we\'ll map by position:');
        headers.forEach((header, index) => {
          let expectedField = '';
          switch(index) {
            case 0: expectedField = 'Application ID'; break;
            case 1: expectedField = 'Application Name'; break;
            case 2: expectedField = 'Total Annual Cost'; break;
            case 3: expectedField = 'Software Licensing'; break;
            case 4: expectedField = 'Vendor Support'; break;
            case 5: expectedField = 'Infrastructure'; break;
            case 6: expectedField = 'Operational Costs'; break;
            case 7: expectedField = 'Cloud/SaaS'; break;
            default: expectedField = 'Unknown'; break;
          }
          console.log(`  Column ${index}: "${header}" → ${expectedField}`);
        });
        
        if (headers.length === 0) {
          alert('No headers detected. Please check your file format.');
          return;
        }
        
        const data = lines.slice(1).map((line, index) => {
          console.log(`📝 RAW LINE ${index + 1}: "${line}"`);
          
          // Skip completely empty lines
          if (!line || line.trim() === '') {
            console.log(`⚠️ Skipping empty line ${index + 1}`);
            return null;
          }
          
          const values = parseCSVLine(line).map(v => 
            v.replace(/['"]/g, '').trim()
          );
          
          console.log(`📊 PARSED VALUES for line ${index + 1}:`, values);
          console.log(`📊 Values count: ${values.length}, Headers count: ${headers.length}`);
          
          const obj: any = {};
          
          headers.forEach((header, headerIndex) => {
            const value = values[headerIndex] || '';
            obj[header] = value;
            console.log(`  [${headerIndex}] "${header}" = "${value}"`);
          });
          
          return obj;
        }).filter(row => {
          // Filter out null rows (empty lines) and rows without meaningful data
          if (!row) {
            return false;
          }
          
          console.log(`🔍 ROW VALIDATION:`, row);
          console.log(`   Available keys in row:`, Object.keys(row));
          
          // Check if row has actual application data - STRICT validation
          const possibleIdFields = ['Application ID', 'ApplicationID', 'App ID', 'ID'];
          const possibleNameFields = ['Application Name', 'ApplicationName', 'App Name', 'Application'];
          
          let appId = '';
          let appName = '';
          
          // Try to find Application ID - must be non-empty and meaningful
          for (const field of possibleIdFields) {
            if (row[field]) {
              const value = row[field].toString().trim();
              if (value !== '' && value !== 'N/A' && value !== 'null' && value !== 'undefined' && value !== '-') {
                appId = value;
                console.log(`   Found Application ID in field "${field}": "${appId}"`);
                break;
              }
            }
          }
          
          // Try to find Application Name - must be non-empty and meaningful
          for (const field of possibleNameFields) {
            if (row[field]) {
              const value = row[field].toString().trim();
              if (value !== '' && value !== 'N/A' && value !== 'null' && value !== 'undefined' && value !== '-') {
                appName = value;
                console.log(`   Found Application Name in field "${field}": "${appName}"`);
                break;
              }
            }
          }
          
          // STRICT VALIDATION: Must have BOTH Application ID AND Application Name
          const isValidRow = appId !== '' && appName !== '';
          
          console.log(`   Application ID: "${appId}"`);
          console.log(`   Application Name: "${appName}"`);
          console.log(`   Valid Row (has both ID and Name): ${isValidRow}`);
          
          if (!isValidRow) {
          }
          
          return isValidRow;
        });

        console.log('Processed data count:', data.length);

        if (data.length === 0) {
          alert('No valid data rows found in the file. Please check that your file contains data rows with values.');
          return;
        }

        // Transform data to match CurrentRunRateData structure
        const transformedData: CurrentRunRateData[] = data.map((row, index) => {
          console.log(`🔥 PROCESSING ROW ${index + 1}:`);
          console.log(`Raw row data:`, row);
          console.log(`Available headers in row: [${Object.keys(row).join(', ')}]`);
          

          const parseNumber = (value: string, fieldName: string = ''): number => {
            console.log(`\n💰 PARSING NUMBER for ${fieldName}:`);
            console.log(`   Input value: "${value}" (type: ${typeof value})`);
            
            if (!value || value === '' || value === 'N/A' || value === 'null' || value === 'undefined' || value === '-') {
              return 0;
            }
            
            // Convert to string and clean
            const stringValue = String(value).trim();
            console.log(`   String value: "${stringValue}"`);
            
            // Handle quoted values
            let unquotedValue = stringValue.replace(/^["']|["']$/g, '');
            console.log(`   Unquoted value: "${unquotedValue}"`);
            
            // Remove currency symbols, commas, spaces, parentheses, but keep decimals and negative signs
            let cleanValue = unquotedValue
              .replace(/[$£€¥₹,\s%()]/g, '')  // Remove currency symbols and formatting  
              .replace(/[^\d.-]/g, '')        // Keep only digits, decimal points, and negative signs
              .replace(/^[.-]+/, '')          // Remove leading dots or dashes
              .replace(/[.-]+$/, '');         // Remove trailing dots or dashes
            
            console.log(`   Clean value: "${cleanValue}"`);
            
            // Handle empty string after cleaning
            if (cleanValue === '' || cleanValue === '-' || cleanValue === '.' || cleanValue === '..') {
              return 0;
            }
            
            // Handle multiple decimal points - keep only the first one
            const decimalCount = (cleanValue.match(/\./g) || []).length;
            if (decimalCount > 1) {
              console.log(`   ⚠️ Multiple decimal points found, fixing...`);
              const parts = cleanValue.split('.');
              cleanValue = parts[0] + '.' + parts.slice(1).join('');
              console.log(`   Fixed decimal value: "${cleanValue}"`);
            }
            
            const parsed = parseFloat(cleanValue);
            console.log(`   Parsed result: ${parsed} (isNaN: ${isNaN(parsed)})`);
            
            if (isNaN(parsed)) {
              return 0;
            }
            
            // Round to 2 decimal places to handle floating point precision
            const result = Math.round(parsed * 100) / 100;
            return result;
          };

          // ROBUST APPROACH: Extract values by header matching with fallback to position
          console.log(`🔍 ROW ${index + 1} OBJECT KEYS:`, Object.keys(row));
          console.log(`📊 ROW ${index + 1} FULL ROW DATA:`, row);
          console.log(`🎯 ROW ${index + 1} OBJECT VALUES:`, Object.values(row));
          
          // Define expected headers in order
          const headerMappings = [
            ['Application ID', 'ApplicationID', 'App ID', 'ID'],
            ['Application Name', 'ApplicationName', 'App Name', 'Application'],
            ['Total Annual Cost to Operate and Support', 'Total Annual Cost', 'TotalCost', 'Total Cost'],
            ['Annual Software Licensing Fees', 'Software Licensing', 'Software', 'Licensing'],
            ['Annual 3rd Party Vendor Support Cost', 'Vendor Support', 'Third Party', '3rd Party'],
            ['Annual Infrastructure Cost', 'Infrastructure Cost', 'Infrastructure', 'Infra'],
            ['Indirect Operational Costs', 'Operational Costs', 'Operations', 'Ops'],
            ['Cloud/SaaS Subscription Costs', 'Cloud/SaaS', 'Cloud', 'SaaS']
          ];
          
          // Extract values by trying to match headers, fallback to positional
          const getValueByHeaderOrPosition = (headerOptions: string[], position: number, defaultValue: string = ''): string => {
            console.log(`\n🔎 Looking for field at position ${position} with headers: [${headerOptions.join(', ')}]`);
            
            // Try to find by header match first
            for (const header of headerOptions) {
              if (row[header] !== undefined && row[header] !== null) {
                const value = String(row[header]).trim();
                return value;
              } else {
              }
            }
            
            // Fallback to positional extraction
            const values = Object.values(row);
            console.log(`  📊 Available values: [${values.map(v => `"${v}"`).join(', ')}]`);
            if (values[position] !== undefined && values[position] !== null) {
              const value = String(values[position]).trim();
              console.log(`  📍 Found by position ${position} = "${value}" (type: ${typeof values[position]})`);
              return value;
            }
            
            return defaultValue;
          };
          
          const appId = getValueByHeaderOrPosition(headerMappings[0], 0);
          const appName = getValueByHeaderOrPosition(headerMappings[1], 1);
          const totalAnnualCostValue = getValueByHeaderOrPosition(headerMappings[2], 2);
          const softwareLicensingValue = getValueByHeaderOrPosition(headerMappings[3], 3);
          const vendorSupportValue = getValueByHeaderOrPosition(headerMappings[4], 4);
          const infrastructureValue = getValueByHeaderOrPosition(headerMappings[5], 5);
          const operationalCostsValue = getValueByHeaderOrPosition(headerMappings[6], 6);
          const cloudSaasValue = getValueByHeaderOrPosition(headerMappings[7], 7);
          
          console.log(`🔍 FINAL EXTRACTED VALUES - Row ${index + 1}:`);
          console.log(`  Application ID: "${appId}"`);
          console.log(`  Application Name: "${appName}"`);
          console.log(`  Total Annual Cost: "${totalAnnualCostValue}"`);
          console.log(`  Software Licensing: "${softwareLicensingValue}"`);
          console.log(`  Vendor Support: "${vendorSupportValue}"`);
          console.log(`  Infrastructure: "${infrastructureValue}"`);
          console.log(`  Operational Costs: "${operationalCostsValue}"`);
          console.log(`  Cloud/SaaS: "${cloudSaasValue}"`);

          return {
            id: `current_run_rate_${Date.now()}_${index}`,
            applicationId: appId || `APP_${index + 1}`,
            applicationName: appName || `Application_${index + 1}`,
            totalAnnualCost: parseNumber(totalAnnualCostValue, 'Total Annual Cost'),
            annualSoftwareLicensingFees: parseNumber(softwareLicensingValue, 'Software Licensing'),
            annualThirdPartyVendorSupport: parseNumber(vendorSupportValue, 'Vendor Support'),
            annualInfrastructureCost: parseNumber(infrastructureValue, 'Infrastructure'),
            indirectOperationalCosts: parseNumber(operationalCostsValue, 'Operational Costs'),
            cloudSaasSubscriptionCostImpact: parseNumber(cloudSaasValue, 'Cloud/SaaS')
          };
        });

        console.log(`\n🎉 TRANSFORMATION COMPLETE:`);
        console.log(`   Processed ${transformedData.length} records`);
        console.log(`   Sample transformed data:`, transformedData[0]);
        
        // Log summary of non-zero values to help debug
        transformedData.forEach((record, idx) => {
          const nonZeroFields = [];
          if (record.totalAnnualCost > 0) nonZeroFields.push(`Total: $${record.totalAnnualCost}`);
          if (record.annualSoftwareLicensingFees > 0) nonZeroFields.push(`Software: $${record.annualSoftwareLicensingFees}`);
          if (record.annualThirdPartyVendorSupport > 0) nonZeroFields.push(`Vendor: $${record.annualThirdPartyVendorSupport}`);
          if (record.annualInfrastructureCost > 0) nonZeroFields.push(`Infra: $${record.annualInfrastructureCost}`);
          if (record.indirectOperationalCosts > 0) nonZeroFields.push(`Ops: $${record.indirectOperationalCosts}`);
          if (record.cloudSaasSubscriptionCostImpact > 0) nonZeroFields.push(`Cloud: $${record.cloudSaasSubscriptionCostImpact}`);
          
          if (nonZeroFields.length > 0) {
            console.log(`   Row ${idx + 1} (${record.applicationName}): ${nonZeroFields.join(', ')}`);
          } else {
            console.log(`   ⚠️ Row ${idx + 1} (${record.applicationName}): ALL VALUES ARE ZERO`);
          }
        });

        setUploadedData(transformedData);
        setShowPreview(true);
        
        console.log(`🎉 READY FOR PREVIEW: ${transformedData.length} records prepared for import`);
        console.log('Preview data sample:', transformedData.slice(0, 2));
      } catch (error) {
        console.error('Error parsing file:', error);
        alert(`Error parsing file: ${error instanceof Error ? error.message : 'Unknown error'}. Please check the file format and try again.`);
      }
    };
    
    reader.readAsText(file, 'UTF-8');
  };

  const handleUpdateFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    console.log('📁 File selected:', file.name);

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a CSV file (.csv)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        let text = event.target?.result as string;
        
        // Handle potential encoding issues
        if (text.includes('\uFFFD')) {
          text = text.replace(/[^\x00-\x7F]/g, '');
        }
        
        // Handle BOM if present
        if (text.charCodeAt(0) === 0xFEFF) {
          text = text.slice(1);
        }
        
        // Normalize line endings
        text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        
        const parseCSVLine = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;
          let quoteChar = '';
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            if ((char === '"' || char === "'") && !inQuotes) {
              inQuotes = true;
              quoteChar = char;
            } else if (char === quoteChar && inQuotes) {
              if (i + 1 < line.length && line[i + 1] === quoteChar) {
                current += char;
                i++;
              } else {
                inQuotes = false;
                quoteChar = '';
              }
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else if (char !== '\r') {
              current += char;
            }
          }
          
          result.push(current.trim());
          return result;
        };

        const allLines = text.split('\n');
        const lines = allLines.filter(line => {
          const trimmed = line.trim();
          return trimmed && trimmed !== '' && !trimmed.match(/^,+$/);
        });
        
        if (lines.length < 2) {
          alert('File appears to be empty or contains only headers.');
          return;
        }

        const headers = parseCSVLine(lines[0]).map(h => h.replace(/['"]/g, '').trim());
        
        const data = lines.slice(1).map((line, index) => {
          if (!line || line.trim() === '') return null;
          
          const values = parseCSVLine(line).map(v => v.replace(/['"]/g, '').trim());
          const obj: any = {};
          
          headers.forEach((header, headerIndex) => {
            obj[header] = values[headerIndex] || '';
          });
          
          return obj;
        }).filter(row => {
          if (!row) return false;
          
          const possibleIdFields = ['Application ID', 'ApplicationID', 'App ID', 'ID'];
          const possibleNameFields = ['Application Name', 'ApplicationName', 'App Name', 'Application'];
          
          let appId = '';
          let appName = '';
          
          for (const field of possibleIdFields) {
            if (row[field]) {
              const value = row[field].toString().trim();
              if (value && value !== 'N/A' && value !== 'null' && value !== '-') {
                appId = value;
                break;
              }
            }
          }
          
          for (const field of possibleNameFields) {
            if (row[field]) {
              const value = row[field].toString().trim();
              if (value && value !== 'N/A' && value !== 'null' && value !== '-') {
                appName = value;
                break;
              }
            }
          }
          
          return appId !== '' && appName !== '';
        });

        if (data.length === 0) {
          alert('No valid data rows found in the file.');
          return;
        }

        // Transform data for updates
        const transformedData = data.map((row, index) => {
          const parseNumber = (value: string): number => {
            if (!value || value === '' || value === 'N/A' || value === 'null' || value === '-') {
              return 0;
            }
            
            const stringValue = String(value).trim();
            const unquotedValue = stringValue.replace(/^["']|["']$/g, '');
            let cleanValue = unquotedValue
              .replace(/[$£€¥₹,\s%()]/g, '')
              .replace(/[^\d.-]/g, '')
              .replace(/^[.-]+/, '')
              .replace(/[.-]+$/, '');
            
            if (cleanValue === '' || cleanValue === '-' || cleanValue === '.') {
              return 0;
            }
            
            const decimalCount = (cleanValue.match(/\./g) || []).length;
            if (decimalCount > 1) {
              const parts = cleanValue.split('.');
              cleanValue = parts[0] + '.' + parts.slice(1).join('');
            }
            
            const parsed = parseFloat(cleanValue);
            return isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
          };

          const headerMappings = [
            ['Application ID', 'ApplicationID', 'App ID', 'ID'],
            ['Application Name', 'ApplicationName', 'App Name', 'Application'],
            ['Total Annual Cost to Operate and Support', 'Total Annual Cost', 'TotalCost', 'Total Cost'],
            ['Annual Software Licensing Fees', 'Software Licensing', 'Software', 'Licensing'],
            ['Annual 3rd Party Vendor Support Cost', 'Vendor Support', 'Third Party', '3rd Party'],
            ['Annual Infrastructure Cost', 'Infrastructure Cost', 'Infrastructure', 'Infra'],
            ['Indirect Operational Costs', 'Operational Costs', 'Operations', 'Ops'],
            ['Cloud/SaaS Subscription Costs', 'Cloud/SaaS', 'Cloud', 'SaaS']
          ];
          
          const getValueByHeaderOrPosition = (headerOptions: string[], position: number): string => {
            for (const header of headerOptions) {
              if (row[header] !== undefined && row[header] !== null) {
                return String(row[header]).trim();
              }
            }
            
            const values = Object.values(row);
            if (values[position] !== undefined && values[position] !== null) {
              return String(values[position]).trim();
            }
            
            return '';
          };
          
          const appId = getValueByHeaderOrPosition(headerMappings[0], 0);
          const appName = getValueByHeaderOrPosition(headerMappings[1], 1);
          const totalAnnualCost = getValueByHeaderOrPosition(headerMappings[2], 2);
          const softwareLicensing = getValueByHeaderOrPosition(headerMappings[3], 3);
          const vendorSupport = getValueByHeaderOrPosition(headerMappings[4], 4);
          const infrastructure = getValueByHeaderOrPosition(headerMappings[5], 5);
          const operationalCosts = getValueByHeaderOrPosition(headerMappings[6], 6);
          const cloudSaas = getValueByHeaderOrPosition(headerMappings[7], 7);

          return {
            applicationId: appId,
            applicationName: appName,
            totalAnnualCost: parseNumber(totalAnnualCost),
            annualSoftwareLicensingFees: parseNumber(softwareLicensing),
            annualThirdPartyVendorSupport: parseNumber(vendorSupport),
            annualInfrastructureCost: parseNumber(infrastructure),
            indirectOperationalCosts: parseNumber(operationalCosts),
            cloudSaasSubscriptionCostImpact: parseNumber(cloudSaas)
          };
        });

        console.log('📊 Processing complete:', transformedData.length, 'records');
        setUploadedUpdateData(transformedData);
        setShowUpdatePreview(true);
        
      } catch (error) {
        console.error('❌ Error parsing update file:', error);
        alert(`Error parsing file: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };
    
    reader.readAsText(file, 'UTF-8');
  };

  const processUpdateDataDirectly = async () => {
    console.log('Processing Current Run Rate update data:', uploadedUpdateData);
    setLoading(true);
    
    try {
      // Call API to bulk update current run rate data
      const result = await apiService.bulkUpdateCurrentRunRateData(uploadedUpdateData);
      
      // Clear update state and close preview
      setUploadedUpdateData([]);
      setShowUpdatePreview(false);
      if (updateFileInputRef.current) {
        updateFileInputRef.current.value = '';
      }
      
      // Reload data from database
      await loadCurrentRunRateData();
      
      if (result.success) {
        alert(`Successfully updated ${result.updated || 0} current run rate records`);
      } else {
        alert(`Update completed with issues: ${result.message || 'Unknown error'}`);
      }
      
    } catch (error: any) {
      console.error('Error updating current run rate data:', error);
      alert(`Error updating data: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const processUploadedData = async () => {
    console.log('Processing uploaded Current Run Rate data:', uploadedData);
    setLoading(true);
    
    try {
      // Call API to bulk import current run rate data
      const result = await apiService.bulkImportCurrentRunRateData(uploadedData);
      
      // Clear local upload state and close preview
      setUploadedData([]);
      setShowPreview(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Reload data from database
      await loadCurrentRunRateData();
      
      if (result.errors && result.errors.length > 0) {
        alert(`Import completed with ${result.errors.length} errors:\n${result.imported} imported, ${result.updated} updated\n\nErrors:\n${result.errors.slice(0, 5).join('\n')}${result.errors.length > 5 ? '\n...and more' : ''}`);
      } else {
        alert(`Successfully imported ${result.imported} new records and updated ${result.updated} existing records to the database`);
      }
      
    } catch (error: any) {
      console.error('Error importing current run rate data:', error);
      alert(`Error importing data: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllData = () => {
    setShowDeleteModal(true);
    setDeletePassword('');
  };

  const handleConfirmDeleteAll = async () => {
    if (deletePassword !== 'ABC123') {
      alert('Incorrect password. Access denied.');
      return;
    }

    setLoading(true);
    try {
      // Call API to delete all current run rate data
      const result = await apiService.deleteAllCurrentRunRateData();
      
      setShowDeleteModal(false);
      setDeletePassword('');
      
      // Reload data from database
      await loadCurrentRunRateData();
      
      alert(`Successfully deleted ${result.deletedCount} current run rate records from the database`);
    } catch (error: any) {
      console.error('Error deleting current run rate data:', error);
      alert(`Error deleting data: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeleteAll = () => {
    setShowDeleteModal(false);
    setDeletePassword('');
  };

  const subCostFields = ['annualSoftwareLicensingFees', 'annualThirdPartyVendorSupport', 'annualInfrastructureCost', 'indirectOperationalCosts', 'cloudSaasSubscriptionCostImpact'];
  const costFields = ['totalAnnualCost', ...subCostFields];

  const calcSubCostSum = (data: typeof formData, overrideField?: string, overrideValue?: number): number => {
    return subCostFields.reduce((sum, field) => {
      const val = field === overrideField ? (overrideValue || 0) : (data as any)[field];
      return sum + (parseFloat(val) || 0);
    }, 0);
  };

  const isTotalMismatched = (data: typeof formData): boolean => {
    const subSum = calcSubCostSum(data);
    // If all sub-costs are 0, no mismatch (user entered total directly)
    if (subSum === 0) return false;
    return Math.abs(data.totalAnnualCost - subSum) > 0.01;
  };

  const TOTAL_MATCH_COLOR = '#1b7a2b';
  const TOTAL_MISMATCH_COLOR = '#dc2626';

  const getTotalColor = (record: CurrentRunRateData | typeof formData): string => {
    const subSum = (record.annualSoftwareLicensingFees || 0) +
      (record.annualThirdPartyVendorSupport || 0) +
      (record.annualInfrastructureCost || 0) +
      (record.indirectOperationalCosts || 0) +
      (record.cloudSaasSubscriptionCostImpact || 0);
    if (subSum === 0) return TOTAL_MATCH_COLOR;
    return Math.abs((record.totalAnnualCost || 0) - subSum) > 0.01 ? TOTAL_MISMATCH_COLOR : TOTAL_MATCH_COLOR;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (costFields.includes(name)) {
      // Allow empty, digits, one decimal point
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setActiveCostFieldValue(value);
        setFormData(prev => ({
          ...prev,
          [name]: value === '' ? 0 : parseFloat(value) || 0
        }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));

    // If application is selected, auto-fill the application name
    if (name === 'applicationId') {
      const selectedApp = availableApplications.find(app => app.application_id_code === value);
      if (selectedApp) {
        setFormData(prev => ({ ...prev, applicationName: selectedApp.name }));
      }
    }
  };

  const handleCostFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    if (!costFields.includes(name)) return;
    setActiveCostField(name);
    const currentVal = (formData as any)[name];
    // Show empty if 0 so user can type freely
    setActiveCostFieldValue(currentVal === 0 ? '' : String(currentVal));
  };

  const handleCostFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    // Commit the final numeric value
    const finalValue = parseFloat(activeCostFieldValue) || 0;
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    setActiveCostField(null);
    setActiveCostFieldValue('');

    // Trigger recalc popup for sub-cost fields
    if (subCostFields.includes(name)) {
      const newFormData = { ...formData, [name]: finalValue };
      const newSum = subCostFields.reduce((sum, f) => sum + ((newFormData as any)[f] || 0), 0);
      if (Math.abs(newFormData.totalAnnualCost - newSum) > 0.01) {
        recalcPendingRef.current = true;
        setPendingRecalcValue(newSum);
        setShowRecalcConfirm(true);
      }
    }
  };

  const getCostFieldDisplayValue = (fieldName: string): string => {
    if (activeCostField === fieldName) {
      return activeCostFieldValue;
    }
    const val = (formData as any)[fieldName];
    return val === 0 ? '0' : String(val);
  };


  const handleRecalcConfirmYes = () => {
    setFormData(prev => ({ ...prev, totalAnnualCost: pendingRecalcValue }));
    setShowRecalcConfirm(false);
    recalcPendingRef.current = false;
  };

  const handleRecalcConfirmNo = () => {
    setShowRecalcConfirm(false);
    recalcPendingRef.current = false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If recalc dialog is showing or pending, don't submit — let user answer first
    if (showRecalcConfirm || recalcPendingRef.current) return;

    try {
      // Auto-set tcoStatus based on cost values
      const autoStatus = formData.totalAnnualCost > 0 ? 'Defined' : formData.tcoStatus;
      const costData = {
        applicationId: formData.applicationId,
        applicationName: formData.applicationName,
        totalAnnualCost: formData.totalAnnualCost,
        annualSoftwareLicensingFees: formData.annualSoftwareLicensingFees,
        annualThirdPartyVendorSupport: formData.annualThirdPartyVendorSupport,
        annualInfrastructureCost: formData.annualInfrastructureCost,
        indirectOperationalCosts: formData.indirectOperationalCosts,
        cloudSaasSubscriptionCostImpact: formData.cloudSaasSubscriptionCostImpact,
        tcoStatus: autoStatus
      };

      if (editingCostRecord) {
        await apiService.updateCurrentRunRateData(editingCostRecord.id, costData);
        alert('Cost record updated successfully!');
      } else {
        await apiService.createCurrentRunRateData(costData);
        alert('Cost record created successfully!');
      }
      
      resetForm();
      await loadCurrentRunRateData();
      await loadAvailableApplications();
    } catch (error: any) {
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      alert(`Error ${editingCostRecord ? 'updating' : 'creating'} cost record: ${errorMessage}`);
    }
  };

  const resetForm = () => {
    setFormData({
      applicationId: '',
      applicationName: '',
      totalAnnualCost: 0,
      annualSoftwareLicensingFees: 0,
      annualThirdPartyVendorSupport: 0,
      annualInfrastructureCost: 0,
      indirectOperationalCosts: 0,
      cloudSaasSubscriptionCostImpact: 0,
      tcoStatus: 'Not Defined'
    });
    setShowForm(false);
    setEditingCostRecord(null);
    setViewingCostRecord(null);
  };

  const handleEdit = (costRecord: CurrentRunRateData) => {
    setEditingCostRecord(costRecord);
    setFormData({
      applicationId: costRecord.applicationId,
      applicationName: costRecord.applicationName,
      totalAnnualCost: costRecord.totalAnnualCost,
      annualSoftwareLicensingFees: costRecord.annualSoftwareLicensingFees,
      annualThirdPartyVendorSupport: costRecord.annualThirdPartyVendorSupport,
      annualInfrastructureCost: costRecord.annualInfrastructureCost,
      indirectOperationalCosts: costRecord.indirectOperationalCosts,
      cloudSaasSubscriptionCostImpact: costRecord.cloudSaasSubscriptionCostImpact,
      tcoStatus: costRecord.tcoStatus || 'Not Defined'
    });
    setShowForm(true);
  };

  const handleView = (costRecord: CurrentRunRateData) => {
    setViewingCostRecord(costRecord);
    setFormData({
      applicationId: costRecord.applicationId,
      applicationName: costRecord.applicationName,
      totalAnnualCost: costRecord.totalAnnualCost,
      annualSoftwareLicensingFees: costRecord.annualSoftwareLicensingFees,
      annualThirdPartyVendorSupport: costRecord.annualThirdPartyVendorSupport,
      annualInfrastructureCost: costRecord.annualInfrastructureCost,
      indirectOperationalCosts: costRecord.indirectOperationalCosts,
      cloudSaasSubscriptionCostImpact: costRecord.cloudSaasSubscriptionCostImpact,
      tcoStatus: costRecord.tcoStatus || 'Not Defined'
    });
    setShowForm(true);
  };

  const handleDelete = async (applicationId: string) => {
    const costRecord = currentRunRateData.find(record => record.applicationId === applicationId);
    const appName = costRecord?.applicationName || 'this cost record';
    
    if (window.confirm(`Are you sure you want to delete cost record for ${appName}?`)) {
      try {
        await apiService.deleteCurrentRunRateData(applicationId);
        alert('Cost record deleted successfully!');
        await loadCurrentRunRateData();
        await loadAvailableApplications();
      } catch (error: any) {
        alert(`Error deleting cost record: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'Application ID',
      'Application Name',
      'Total Annual Cost to Operate and Support',
      'Annual Software Licensing Fees',
      'Annual 3rd Party Vendor Support Cost',
      'Annual Infrastructure Cost',
      'Indirect Operational Costs',
      'Cloud/SaaS Subscription Costs'
    ];

    const csvContent = headers.join(',') + '\n' +
      'APP001,Sample Application,120000,45000,25000,30000,15000,5000\n' +
      'APP002,Another Application,95000,30000,20000,25000,12000,8000\n' +
      'APP003,Third Application,85000,25000,15000,20000,10000,6000';

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'current_run_rate_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadCurrentRunRateData = () => {
    if (currentRunRateData.length === 0) {
      alert('No current run rate data to download');
      return;
    }

    const headers = [
      'Application ID',
      'Application Name',
      'Total Annual Cost to Operate and Support',
      'Annual Software Licensing Fees',
      'Annual 3rd Party Vendor Support Cost',
      'Annual Infrastructure Cost',
      'Indirect Operational Costs',
      'Cloud/SaaS Subscription Costs'
    ];
    const csvRows = [headers.join(',')];

    currentRunRateData.forEach(record => {
      const row = [
        `"${(record.applicationId || '').replace(/"/g, '""')}"`,
        `"${(record.applicationName || '').replace(/"/g, '""')}"`,
        record.totalAnnualCost || 0,
        record.annualSoftwareLicensingFees || 0,
        record.annualThirdPartyVendorSupport || 0,
        record.annualInfrastructureCost || 0,
        record.indirectOperationalCosts || 0,
        record.cloudSaasSubscriptionCostImpact || 0
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `current_run_rate_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };


  const formatLargeCurrency = (amount: number) => {
    if (amount === 0) return '$0';
    
    if (amount >= 1000000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(amount / 1000000) + 'M';
    } else if (amount >= 1000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount / 1000) + 'K';
    }
    
    return formatCurrency(amount);
  };

  // Filter cost records based on search text
  const filteredCurrentRunRateData = (() => {
    const textFiltered = currentRunRateData.filter(record => {
      if (!filterText.trim()) return true;
      const searchText = filterText.toLowerCase();
      return (
        record.applicationId.toLowerCase().includes(searchText) ||
        record.applicationName.toLowerCase().includes(searchText) ||
        formatCurrency(record.totalAnnualCost).toLowerCase().includes(searchText) ||
        formatCurrency(record.annualSoftwareLicensingFees).toLowerCase().includes(searchText) ||
        formatCurrency(record.annualThirdPartyVendorSupport).toLowerCase().includes(searchText) ||
        formatCurrency(record.annualInfrastructureCost).toLowerCase().includes(searchText) ||
        formatCurrency(record.indirectOperationalCosts).toLowerCase().includes(searchText) ||
        formatCurrency(record.cloudSaasSubscriptionCostImpact).toLowerCase().includes(searchText)
      );
    });
    if (!filterText.trim()) return textFiltered;
    // Expand: include all children of matched parent apps
    const filteredIds = new Set(textFiltered.map(r => r.applicationId));
    const parentCodesInResults = new Set(
      textFiltered.filter(r => r.isParent).map(r => r.applicationId)
    );
    if (parentCodesInResults.size === 0) return textFiltered;
    const extraChildren = currentRunRateData.filter(r =>
      r.parentApplicationCode &&
      parentCodesInResults.has(r.parentApplicationCode) &&
      !filteredIds.has(r.applicationId)
    );
    return [...textFiltered, ...extraChildren];
  })();

  // Clear filter function
  const clearFilter = () => {
    setFilterText('');
  };

  return (
    <div className="page page-fixed-header">
      {/* Fixed Header Section - Does not scroll */}
      <div className="fixed-header-section">
        {/* Header */}
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Current Run Rate</h1>
            </div>
            <div className="page-header__actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowForm(true)}
                disabled={loading}
              >
                ➕ Add Cost
              </button>
              <button
                className="btn btn-outline-info"
                onClick={downloadTemplate}
              >
                📋 Download Template
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
              >
                📤 Upload File
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => updateFileInputRef.current?.click()}
                disabled={loading}
              >
                📤 Update via File
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteAllData}
                disabled={currentRunRateData.length === 0 || loading}
              >
                🗑️ Delete All Data
              </button>
              {currentRunRateData.length > 0 && (
                <button
                  className="btn btn-outline-success"
                  onClick={downloadCurrentRunRateData}
                  title="Download current run rate data as CSV"
                  style={{ border: '2px solid #28a745' }}
                >
                  📥 Download Data
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Statistics - Right below header (excluding parent applications from cost calculations) */}
        <div className="master-data-stats">
          <div className="stat-item">
            <span className="stat-value">{filteredCurrentRunRateData.filter(app => !app.isParent).length.toLocaleString('en-US')}</span>
            <span className="stat-label">{filterText ? 'Filtered' : 'Total'} Applications</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {formatLargeCurrency(filteredCurrentRunRateData.filter(app => !app.isParent).reduce((sum, app) => sum + app.totalAnnualCost, 0))}
            </span>
            <span className="stat-label">Total Annual Operating Cost</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {formatLargeCurrency(filteredCurrentRunRateData.filter(app => !app.isParent).reduce((sum, app) => sum + app.annualSoftwareLicensingFees, 0))}
            </span>
            <span className="stat-label">Total Software Licensing</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {formatLargeCurrency(filteredCurrentRunRateData.filter(app => !app.isParent).reduce((sum, app) => sum + app.annualInfrastructureCost, 0))}
            </span>
            <span className="stat-label">Total Infrastructure Cost</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">
              {formatLargeCurrency(filteredCurrentRunRateData.filter(app => !app.isParent).reduce((sum, app) => sum + app.cloudSaasSubscriptionCostImpact, 0))}
            </span>
            <span className="stat-label">Total Cloud/SaaS Costs</span>
          </div>
        </div>

        {/* Filter Section - Below Statistics */}
        <div style={{ marginBottom: '0', display: 'flex', gap: '12px', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #dee2e6' }}>
          <div style={{ position: 'relative', flex: '1', maxWidth: '400px' }}>
            <input
              type="text"
              placeholder="Filter by application ID, name, or cost amounts..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 30px 6px 10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '15px'
              }}
            />
            {filterText && (
              <button
                onClick={clearFilter}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#666'
                }}
                title="Clear filter"
              >
                ×
              </button>
            )}
          </div>
          {filterText && (
            <div style={{ fontSize: '13px', color: '#666' }}>
              Showing {filteredCurrentRunRateData.length.toLocaleString('en-US')} of {currentRunRateData.length.toLocaleString('en-US')} cost records
            </div>
          )}
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={updateFileInputRef}
        onChange={handleUpdateFileUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />

      {/* Update Preview Modal */}
      {showUpdatePreview && (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => { setShowUpdatePreview(false); setUploadedUpdateData([]); if (updateFileInputRef.current) updateFileInputRef.current.value = ''; }}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '900px',
              maxWidth: '95vw',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative',
              zIndex: 1101
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                Preview Current Run Rate Updates
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {uploadedUpdateData.length} records will be updated
              </p>
            </div>

            <div style={{ marginBottom: '24px', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Application ID</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Application Name</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Total Annual Cost</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Software Licensing</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Vendor Support</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Infrastructure</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Operational Costs</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Cloud/SaaS</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedUpdateData.slice(0, 10).map((row, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px 8px', textAlign: 'left' }}>{row.applicationId}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'left' }}>{row.applicationName}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace', color: getTotalColor(row), fontWeight: '600' }}>{formatCurrency(row.totalAnnualCost)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.annualSoftwareLicensingFees)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.annualThirdPartyVendorSupport)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.annualInfrastructureCost)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.indirectOperationalCosts)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.cloudSaasSubscriptionCostImpact)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {uploadedUpdateData.length > 10 && (
                <p style={{ marginTop: '12px', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
                  Showing first 10 records. {uploadedUpdateData.length - 10} more records will be updated.
                </p>
              )}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              paddingTop: '16px',
              borderTop: '1px solid #eee'
            }}>
              <button
                type="button"
                onClick={() => { setShowUpdatePreview(false); setUploadedUpdateData([]); if (updateFileInputRef.current) updateFileInputRef.current.value = ''; }}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#666',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={processUpdateDataDirectly}
                disabled={loading}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#1976d2',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = '#1565c0')}
                onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = '#1976d2')}
              >
                {loading ? 'Updating...' : `Update ${uploadedUpdateData.length} Records`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Preview Modal */}
      {showPreview && (
        <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => { setShowPreview(false); setUploadedData([]); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '900px',
              maxWidth: '95vw',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative',
              zIndex: 1101
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                Preview Current Run Rate Data
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {uploadedData.length} records will be imported
              </p>
            </div>

            <div style={{ marginBottom: '24px', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Application ID</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Application Name</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Total Annual Cost</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Software Licensing</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Vendor Support</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Infrastructure</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Operational Costs</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontWeight: '600' }}>Cloud/SaaS</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedData.slice(0, 10).map((row, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px 8px', textAlign: 'left' }}>{row.applicationId}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'left' }}>{row.applicationName}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace', color: getTotalColor(row), fontWeight: '600' }}>{formatCurrency(row.totalAnnualCost)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.annualSoftwareLicensingFees)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.annualThirdPartyVendorSupport)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.annualInfrastructureCost)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.indirectOperationalCosts)}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(row.cloudSaasSubscriptionCostImpact)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {uploadedData.length > 10 && (
                <p style={{ marginTop: '12px', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
                  Showing first 10 records. {uploadedData.length - 10} more records will be imported.
                </p>
              )}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              paddingTop: '16px',
              borderTop: '1px solid #eee'
            }}>
              <button
                type="button"
                onClick={() => { setShowPreview(false); setUploadedData([]); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#666',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={processUploadedData}
                disabled={loading}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#1976d2',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = '#1565c0')}
                onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = '#1976d2')}
              >
                {loading ? 'Importing...' : `Import ${uploadedData.length} Records`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Data Modal */}
      {showDeleteModal && (
        <div className="form-modal">
          <div className="form-modal-content">
            <div className="form-modal-header">
              <h2>⚠️ Delete All Current Run Rate Data</h2>
              <button 
                className="btn-close"
                onClick={handleCancelDeleteAll}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="delete-modal-content">
              <div className="warning-message">
                <div className="warning-icon">🚨</div>
                <div className="warning-text">
                  <h3>CRITICAL ACTION</h3>
                  <p>You are about to permanently delete <strong>all current run rate data</strong> from the database.</p>
                  <p>This action cannot be undone and will remove:</p>
                  <ul>
                    <li>All application cost data</li>
                    <li>All financial metrics</li>
                    <li>All operational cost information</li>
                    <li>All infrastructure cost data</li>
                  </ul>
                </div>
              </div>
              
              <div className="password-section">
                <label htmlFor="deletePassword">Enter password to confirm deletion:</label>
                <input
                  type="password"
                  id="deletePassword"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter deletion password"
                  className="password-input"
                  autoFocus
                />
                <small className="password-hint">
                  Contact your administrator if you don't have the deletion password.
                </small>
              </div>
              
              <div className="delete-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={handleCancelDeleteAll}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleConfirmDeleteAll}
                  disabled={!deletePassword || loading}
                >
                  {loading ? 'Deleting...' : '🗑️ Delete All Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Form Modal */}
      {showForm && (
        <div className="form-modal">
          <div className="form-modal-content large">
            <div className="form-modal-header">
              <h2>
                {viewingCostRecord ? 'View Cost Record' : 
                 editingCostRecord ? 'Edit Cost Record' : 
                 'Add New Cost Record'}
              </h2>
              <button 
                className="btn-close"
                onClick={resetForm}
                aria-label="Close form"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="application-master-form">
              {/* Application Selection */}
              <div className="form-section">
                <h3>Application Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="applicationId">Application *</label>
                    {editingCostRecord ? (
                      <input
                        type="text"
                        id="applicationId"
                        name="applicationId"
                        value={formData.applicationId}
                        readOnly
                        className="readonly-input"
                      />
                    ) : (
                      <select
                        id="applicationId"
                        name="applicationId"
                        value={formData.applicationId}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select an application...</option>
                        {availableApplications.map(app => (
                          <option key={app.application_id_code} value={app.application_id_code}>
                            {app.application_id_code} - {app.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="applicationName">Application Name *</label>
                    <input
                      type="text"
                      id="applicationName"
                      name="applicationName"
                      value={formData.applicationName}
                      onChange={handleInputChange}
                      required
                      readOnly={!!editingCostRecord}
                      className={editingCostRecord ? "readonly-input" : ""}
                    />
                  </div>
                </div>
              </div>

              {/* Cost Information */}
              <div className="form-section">
                <h3>Cost Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="totalAnnualCost" style={{ color: getTotalColor(formData), fontWeight: '600' }}>Total Annual Cost to Operate and Support</label>
                    {viewingCostRecord ? (
                      <input
                        type="text"
                        id="totalAnnualCost"
                        name="totalAnnualCost"
                        value={formatCurrency(formData.totalAnnualCost)}
                        readOnly
                        className="readonly-input"
                        style={{ color: getTotalColor(formData), fontWeight: '600' }}
                      />
                    ) : (
                      <input
                        type="text"
                        id="totalAnnualCost"
                        name="totalAnnualCost"
                        value={getCostFieldDisplayValue('totalAnnualCost')}
                        onChange={handleInputChange}
                        onFocus={handleCostFieldFocus}
                        onBlur={handleCostFieldBlur}
                        style={{ color: getTotalColor(formData), fontWeight: '600' }}
                      />
                    )}
                    {isTotalMismatched(formData) && !viewingCostRecord && (
                      <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                        Total does not match sum of cost breakdown (${calcSubCostSum(formData).toLocaleString('en-US', { minimumFractionDigits: 2 })})
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="annualSoftwareLicensingFees">Annual Software Licensing Fees</label>
                    {viewingCostRecord ? (
                      <input
                        type="text"
                        id="annualSoftwareLicensingFees"
                        name="annualSoftwareLicensingFees"
                        value={formatCurrency(formData.annualSoftwareLicensingFees)}
                        readOnly
                        className="readonly-input"
                      />
                    ) : (
                      <input
                        type="text"
                        id="annualSoftwareLicensingFees"
                        name="annualSoftwareLicensingFees"
                        value={getCostFieldDisplayValue('annualSoftwareLicensingFees')}
                        onChange={handleInputChange}
                        onFocus={handleCostFieldFocus}
                        onBlur={handleCostFieldBlur}
                      />
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="annualThirdPartyVendorSupport">Annual 3rd Party Vendor Support Cost</label>
                    {viewingCostRecord ? (
                      <input
                        type="text"
                        id="annualThirdPartyVendorSupport"
                        name="annualThirdPartyVendorSupport"
                        value={formatCurrency(formData.annualThirdPartyVendorSupport)}
                        readOnly
                        className="readonly-input"
                      />
                    ) : (
                      <input
                        type="text"
                        id="annualThirdPartyVendorSupport"
                        name="annualThirdPartyVendorSupport"
                        value={getCostFieldDisplayValue('annualThirdPartyVendorSupport')}
                        onChange={handleInputChange}
                        onFocus={handleCostFieldFocus}
                        onBlur={handleCostFieldBlur}
                      />
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="annualInfrastructureCost">Annual Infrastructure Cost</label>
                    {viewingCostRecord ? (
                      <input
                        type="text"
                        id="annualInfrastructureCost"
                        name="annualInfrastructureCost"
                        value={formatCurrency(formData.annualInfrastructureCost)}
                        readOnly
                        className="readonly-input"
                      />
                    ) : (
                      <input
                        type="text"
                        id="annualInfrastructureCost"
                        name="annualInfrastructureCost"
                        value={getCostFieldDisplayValue('annualInfrastructureCost')}
                        onChange={handleInputChange}
                        onFocus={handleCostFieldFocus}
                        onBlur={handleCostFieldBlur}
                      />
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="indirectOperationalCosts">Indirect Operational Costs</label>
                    {viewingCostRecord ? (
                      <input
                        type="text"
                        id="indirectOperationalCosts"
                        name="indirectOperationalCosts"
                        value={formatCurrency(formData.indirectOperationalCosts)}
                        readOnly
                        className="readonly-input"
                      />
                    ) : (
                      <input
                        type="text"
                        id="indirectOperationalCosts"
                        name="indirectOperationalCosts"
                        value={getCostFieldDisplayValue('indirectOperationalCosts')}
                        onChange={handleInputChange}
                        onFocus={handleCostFieldFocus}
                        onBlur={handleCostFieldBlur}
                      />
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="cloudSaasSubscriptionCostImpact">Cloud/SaaS Subscription Costs</label>
                    {viewingCostRecord ? (
                      <input
                        type="text"
                        id="cloudSaasSubscriptionCostImpact"
                        name="cloudSaasSubscriptionCostImpact"
                        value={formatCurrency(formData.cloudSaasSubscriptionCostImpact)}
                        readOnly
                        className="readonly-input"
                      />
                    ) : (
                      <input
                        type="text"
                        id="cloudSaasSubscriptionCostImpact"
                        name="cloudSaasSubscriptionCostImpact"
                        value={getCostFieldDisplayValue('cloudSaasSubscriptionCostImpact')}
                        onChange={handleInputChange}
                        onFocus={handleCostFieldFocus}
                        onBlur={handleCostFieldBlur}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* TCO Status */}
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                  TCO Status
                </label>
                {viewingCostRecord ? (
                  <span style={{
                    padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600',
                    backgroundColor: formData.tcoStatus === 'Defined' ? '#C6EFCE' : formData.tcoStatus === 'Bundled' ? '#BDD7EE' : formData.tcoStatus === 'Free' ? '#E2EFDA' : '#FFC7CE',
                    color: formData.tcoStatus === 'Defined' ? '#006100' : formData.tcoStatus === 'Bundled' ? '#1565c0' : formData.tcoStatus === 'Free' ? '#2e7d32' : '#9C0006'
                  }}>
                    {formData.tcoStatus}
                  </span>
                ) : (
                  <select
                    value={formData.tcoStatus}
                    onChange={(e) => setFormData({ ...formData, tcoStatus: e.target.value })}
                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: '200px' }}
                  >
                    <option value="Not Defined">Not Defined — TCO unknown</option>
                    <option value="Defined">Defined — TCO assigned</option>
                    <option value="Bundled">Bundled — Included with another product ($0)</option>
                    <option value="Free">Free / Open Source ($0)</option>
                  </select>
                )}
                {!viewingCostRecord && formData.tcoStatus === 'Bundled' && (
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#666' }}>
                    This application's cost is covered by another product's license (e.g., Microsoft Edge bundled with Office).
                  </p>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  {viewingCostRecord ? 'Close' : 'Cancel'}
                </button>
                {!viewingCostRecord && (
                  <button type="submit" className="btn btn-primary">
                    {editingCostRecord ? 'Update Cost Record' : 'Create Cost Record'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Scrollable Data Section */}
      <div className="scrollable-data-section" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div className="empty-state">
            <div className="empty-state-icon">⏳</div>
            <h3>Loading Current Run Rate Data</h3>
            <p>Please wait while we fetch the latest cost data from the database...</p>
          </div>
        ) : currentRunRateData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💰</div>
            <h3>No Current Run Rate Data</h3>
            <p>Upload a CSV file with comprehensive application cost breakdown or manually add cost records to get started.</p>
            <div className="empty-state-actions">
              <button 
                className="btn btn-outline-primary"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload File
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => setShowForm(true)}
              >
                Add Cost
              </button>
            </div>
          </div>
        ) : filteredCurrentRunRateData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔍</div>
            <h3>No Cost Records Found</h3>
            <p>No cost records match your current filter criteria.</p>
            <div className="empty-state-actions">
              <button 
                className="btn btn-outline-primary"
                onClick={clearFilter}
              >
                Clear Filter
              </button>
            </div>
          </div>
        ) : (
          <div style={{ border: '1px solid #dee2e6', borderRadius: '0.5rem', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Fixed header */}
            <div style={{ flexShrink: 0 }}>
              <table className="master-data-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <thead>
                  <tr>
                    {[
                      { label: 'Application ID', align: 'left' },
                      { label: 'Application Name', align: 'left' },
                      { label: 'Total Annual Cost', align: 'right' },
                      { label: 'Software Licensing', align: 'right' },
                      { label: '3rd Party Vendor', align: 'right' },
                      { label: 'Infrastructure', align: 'right' },
                      { label: 'Indirect Ops', align: 'right' },
                      { label: 'Cloud/SaaS', align: 'right' },
                      { label: 'Actions', align: 'center' }
                    ].map((col, i) => (
                      <th key={i} style={{
                        backgroundColor: '#e8e8e8',
                        borderBottom: '2px solid #bbb',
                        padding: '10px 8px',
                        fontWeight: 700,
                        fontSize: '13px',
                        color: '#333',
                        textAlign: col.align as any
                      }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <table className="master-data-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
              <tbody>
                {filteredCurrentRunRateData.map(record => (
                    <tr key={record.id} style={record.isParent ? { backgroundColor: '#e3f2fd' } : record.parentApplicationCode ? { backgroundColor: '#f8f9fa' } : undefined}>
                      <td style={{ textAlign: 'left' }}>
                        <strong>{record.applicationId}</strong>
                      </td>
                      <td style={{ textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '8px' }}>
                          <strong>{record.applicationName}</strong>
                          {record.isParent && (
                            <span style={{
                              padding: '2px 8px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              borderRadius: '12px'
                            }}>
                              Parent{record.childCostCount ? ` (${record.childCostCount} children)` : ''}
                            </span>
                          )}
                          {!record.isParent && record.parentApplicationCode && (
                            <span style={{
                              padding: '2px 8px',
                              fontSize: '10px',
                              fontWeight: '500',
                              backgroundColor: '#e3f2fd',
                              color: '#1565c0',
                              borderRadius: '12px'
                            }}>
                              Child of {record.parentApplicationCode}
                            </span>
                          )}
                          {!record.isParent && (
                            <span style={{
                              padding: '2px 6px', borderRadius: '12px', fontSize: '9px', fontWeight: '600', whiteSpace: 'nowrap',
                              backgroundColor: record.tcoStatus === 'Defined' ? '#C6EFCE' : record.tcoStatus === 'Bundled' ? '#BDD7EE' : record.tcoStatus === 'Free' ? '#E2EFDA' : '#FFC7CE',
                              color: record.tcoStatus === 'Defined' ? '#006100' : record.tcoStatus === 'Bundled' ? '#1565c0' : record.tcoStatus === 'Free' ? '#2e7d32' : '#9C0006'
                            }}>
                              {record.tcoStatus || 'Not Defined'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-right currency-cell" style={{ color: getTotalColor(record), fontWeight: '600' }}>{formatCurrency(record.totalAnnualCost)}</td>
                      <td className="text-right currency-cell">{formatCurrency(record.annualSoftwareLicensingFees)}</td>
                      <td className="text-right currency-cell">{formatCurrency(record.annualThirdPartyVendorSupport)}</td>
                      <td className="text-right currency-cell">{formatCurrency(record.annualInfrastructureCost)}</td>
                      <td className="text-right currency-cell">{formatCurrency(record.indirectOperationalCosts)}</td>
                      <td className="text-right currency-cell">{formatCurrency(record.cloudSaasSubscriptionCostImpact)}</td>
                      <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                          <button
                            className="action-btn view-btn"
                            onClick={() => handleView(record)}
                            title="View cost record"
                            style={{
                              padding: '4px 6px',
                              backgroundColor: 'transparent',
                              color: '#1976d2',
                              border: '1px solid #1976d2',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              minWidth: 'auto'
                            }}
                          >
                            👁️
                          </button>
                          <button
                            className="action-btn edit-btn"
                            onClick={() => handleEdit(record)}
                            title="Edit cost record"
                            style={{
                              padding: '4px 6px',
                              backgroundColor: 'transparent',
                              color: '#1976d2',
                              border: '1px solid #1976d2',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              minWidth: 'auto'
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDelete(record.applicationId)}
                            title="Delete cost record"
                            style={{
                              padding: '4px 6px',
                              backgroundColor: 'transparent',
                              color: '#d32f2f',
                              border: '1px solid #d32f2f',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: 'pointer',
                              minWidth: 'auto'
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Recalculate Total Confirmation Popup */}
      {showRecalcConfirm && (
        <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={handleRecalcConfirmNo}>
          <div
            className="modal-content"
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '420px',
              maxWidth: '500px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <h3 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: '500', color: '#1a1a1a' }}>
              Recalculate Total Annual Cost?
            </h3>
            <p style={{ fontSize: '14px', color: '#555', margin: '0 0 8px' }}>
              A cost breakdown field has been updated. Would you like to recalculate the Total Annual Cost from the cost breakdown?
            </p>
            <div style={{
              padding: '12px',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #bae6fd',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#666' }}>Current Total:</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(formData.totalAnnualCost)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#666' }}>New Total (sum of breakdown):</span>
                <span style={{ fontWeight: '600', color: '#1976d2' }}>{formatCurrency(pendingRecalcValue)}</span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={handleRecalcConfirmNo}
                style={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#666',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                No, Keep Current Total
              </button>
              <button
                type="button"
                onClick={handleRecalcConfirmYes}
                style={{
                  padding: '8px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#1976d2',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Yes, Recalculate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentRunRate;